'use client'

import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

/**
 * Deploy-staleness killer. Panel tabs and installed PWAs stay open for days,
 * so new builds never reached operators until they happened to hard-refresh —
 * every feature rollout triggered "bende görünmüyor" support calls.
 *
 * The build id is frozen into this bundle at build time; /api/version always
 * returns the LIVE build's id. On a mismatch:
 *   • tab hidden → reload silently (completely seamless),
 *   • tab visible → show a one-tap "Aktualisieren" banner (never interrupt
 *     someone mid-booking with a surprise reload).
 * Checks run on mount, whenever the tab becomes visible again, and every
 * 5 minutes while open.
 */
const MY_BUILD = process.env.NEXT_PUBLIC_BUILD_ID ?? 'dev'

export function UpdateWatcher() {
  const [updateReady, setUpdateReady] = useState(false)

  useEffect(() => {
    if (MY_BUILD === 'dev') return
    let stopped = false

    async function check() {
      try {
        const r = await fetch('/api/version', { cache: 'no-store' })
        if (!r.ok) return
        const { id } = await r.json() as { id?: string }
        if (stopped || !id || id === 'dev' || id === MY_BUILD) return
        // Per-device preference (Einstellungen → App & Updates): when auto-
        // update is off, never reload on our own — only show the banner.
        let auto = true
        try { auto = localStorage.getItem('rezaro:auto-update') !== '0' } catch { /* private mode */ }
        if (auto && document.visibilityState === 'hidden') {
          window.location.reload()
        } else {
          setUpdateReady(true)
        }
      } catch { /* offline — try again later */ }
    }

    const interval = setInterval(check, 5 * 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVisible)
    // First check shortly after load (not immediately — the page we just
    // got IS the current build in the normal case).
    const t = setTimeout(check, 30_000)

    return () => {
      stopped = true
      clearInterval(interval)
      clearTimeout(t)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!updateReady) return null
  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 rounded-full border border-brand-200 bg-white pl-4 pr-2 py-2 shadow-xl"
      style={{ maxWidth: 'min(94vw, 420px)' }}>
      <span className="text-[13px] font-medium text-neutral-800">Neue Version verfügbar</span>
      <button type="button" onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-brand-700">
        <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
      </button>
    </div>
  )
}
